import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicRequestBuilder';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/useBaseTopicRequestBuilder';
import { useAppSelector } from 'src/utils/Hooks';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { GetAdCampaignTypeMetricsResponse, GetAdCampaignTypeMetricsResponseRecord } from './types';
import axios from 'axios';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { splitCamelCase } from 'src/utils/stringFormatting';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { calculatePercentChange } from 'src/utils/app';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

const TABLE_CELL_FONT_STYLE = { fontSize: '14px' as const };

export function PaidSearchCampaignTypeGrid() {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const retailerId = useRetailerId();
  const baseRequest = useBaseTopicRequestBuilder();

  const adCampaignTypeMetricsRequest = baseRequest
    .setRetailerId(+retailerId)
    .setSortField('adClicks')
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .build();

  const adCampaignTypeMetricsComparisonRequest = new TopicRequestBuilder(adCampaignTypeMetricsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      'PaidSearchTopicsCampaignTypeGrid',
      adCampaignTypeMetricsRequest,
      adCampaignTypeMetricsComparisonRequest
    ],
    queryFn: async () => {
      const endpoint = '/api/beacontopics/GetAdCampaignTypeMetrics';
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetAdCampaignTypeMetricsResponse>(endpoint, adCampaignTypeMetricsRequest),
        axios.post<GetAdCampaignTypeMetricsResponse>(endpoint, adCampaignTypeMetricsComparisonRequest)
      ]);

      return [response, comparisonResponse];
    }
  });

  const [response, comparisonResponse] = data ?? [{ records: [] }, { records: [] }];

  const comparisonCampaignTypeMap = comparisonResponse.records.reduce((acc, record) => {
    acc.set(record.campaignType, record);

    return acc;
  }, new Map<string, GetAdCampaignTypeMetricsResponseRecord>());

  return (
    <EntityTable
      isLoading={isLoading}
      shouldModifyColumnDefs={false}
      rowData={response.records}
      columnDefs={[
        {
          headerName: 'Campaign Type',
          field: 'campaignType',
          valueFormatter: (value: string) => splitCamelCase(value)
        },
        {
          headerName: 'Campaigns',
          field: 'campaigns',
          valueFormatter: (value: number, row: GetAdCampaignTypeMetricsResponseRecord) => {
            const comparisonValue = comparisonCampaignTypeMap.get(row.campaignType)?.campaigns ?? 0;
            const delta = value - comparisonValue;

            return (
              <MetricCell
                metricType={METRICTYPE.VOLUME}
                value={value}
                wrapperStyle={TABLE_CELL_FONT_STYLE}
                comparisonNode={
                  <Text color={delta >= 0 ? 'success' : 'error'} sx={{ fontSize: '14px !important' }}>
                    {`(${delta >= 0 ? '+' : ''}${delta})`}
                  </Text>
                }
              />
            );
          }
        },
        ...(
          [
            ['adClicks', 'Ad\nClicks', METRICTYPE.VOLUME],
            ['adSpend', 'Ad\nSpend', METRICTYPE.MONEY],
            ['adSales', 'Ad\nSales', METRICTYPE.MONEY],
            ['adUnitsSold', 'Ad Units\nSold', METRICTYPE.VOLUME],
            ['roas', 'ROAS', METRICTYPE.MONEY]
          ] as const
        ).map(([field, title, metricType]) => {
          return {
            headerName: title,
            field,
            valueFormatter: (value, row: GetAdCampaignTypeMetricsResponseRecord) => {
              const comparisonValue = comparisonCampaignTypeMap.get(row.campaignType)?.[field] ?? 0;

              return (
                <MetricCell
                  metricType={metricType}
                  value={value}
                  percentChange={calculatePercentChange(value, comparisonValue)}
                  isAdSpendMetric={field === 'adSpend'}
                  wrapperStyle={TABLE_CELL_FONT_STYLE}
                />
              );
            }
          };
        })
      ]}
    />
  );
}
